import {
    ContactFolder,
    createSelector,
    favoritesLeftNavItemId,
    contactsLeftNavItemId,
    deletedItemsLeftNavItemId,
    SparseFolderStateMap,
} from '../../shared';
import type { AppState, ClickedHubPersonaState, FolderErrorStateMap, SearchQuery } from '..';
import { getDisplayedContactFolders } from './contactFoldersSelector';
import { isInSearchMode } from './searchModeSelector';
import { isInDeepLinkMode } from './deeplinkSelector';
import type { EffectiveRightsType } from '../../service';

export { ContactFolder, ClickedHubPersonaState };

/**
 * Returns the id of the menu entry in the left nav that should be marked active.
 * This Id is the folder Id to be refreshed in case of operations that modify the current contact list
 * Undefined is returned if nothing should be marked as active.
 */
export const getActiveLeftNavItemId = createSelector<
    AppState,
    boolean,
    string,
    ClickedHubPersonaState,
    boolean,
    number,
    boolean,
    string
>(
    'getActiveLeftNavItemId',
    [
        isInSearchMode,
        (appState: AppState) => appState.view.navigationState.clickedLeftNavItemId,
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        (appState: AppState) =>
            appState.data.hubState.favoritePersonsState.areInitialFavoritesLoaded,
        (appState: AppState) => appState.data.hubState.favoritePersonsState.favoritePersons.length,
        isInDeepLinkMode,
    ],
    (
        isSearchMode,
        clickedLeftNavItemId,
        clickedPersona,
        areInitialFavoritesLoaded,
        favoritesCount,
        isInDeepLinkMode
    ) => {
        if (isInDeepLinkMode) {
            return clickedLeftNavItemId;
        } else if (
            isSearchMode ||
            (clickedPersona &&
                clickedPersona.origin !== 'personaList' &&
                clickedPersona.origin !== 'unifiedGroupList')
        ) {
            // We are showing search results or the user has clicked a person e.g from an EV.
            return undefined;
        } else if (clickedLeftNavItemId) {
            return clickedLeftNavItemId;
        } else {
            return favoritesCount > 0 || !areInitialFavoritesLoaded
                ? favoritesLeftNavItemId
                : contactsLeftNavItemId;
        }
    }
);

/**
 * Returns true if the clicked contact folder is read-only, false if not.
 * Only folders in the displayed contact folders array are checked, as that
 * is where read-only folders from external networks are found. The root folder
 * is regarded as writable, as it should be possible to delete contacts from it.
 */
export const isActiveLeftNavItemReadOnly = createSelector<
    AppState,
    string,
    ContactFolder[],
    ContactFolder,
    boolean
>(
    'isActiveLeftNavItemReadOnly',
    [
        (appState: AppState) => appState.view.navigationState.clickedLeftNavItemId,
        getDisplayedContactFolders,
        (appState: AppState) => appState.data.contactFoldersState.rootContactFolder,
    ],
    (clickedLeftNavItemId, displayedContactFolders, rootContactFolder) => {
        if (clickedLeftNavItemId) {
            const folder = displayedContactFolders.find(
                displayed => displayed.id === clickedLeftNavItemId
            );
            return !!folder && folder.id !== rootContactFolder.id && folder.isReadOnly;
        }

        return false;
    }
);

export const isActiveLeftNavItemDeletedItems = createSelector<AppState, string, boolean>(
    'isActiveLeftNavItemDeletedItems',
    [getActiveLeftNavItemId],
    activeLeftNavItemId => activeLeftNavItemId && activeLeftNavItemId === deletedItemsLeftNavItemId
);

export const isActiveLeftNavItemPublicFolder = createSelector<
    AppState,
    string,
    ContactFolder[],
    boolean
>(
    'isActiveLeftNavItemPublicFolder',
    [
        getActiveLeftNavItemId,
        (appState: AppState) => appState.data.contactFoldersState.publicFolders,
    ],
    (activeLeftNavItemId, publicFolders) => {
        return publicFolders && publicFolders.some(folder => folder.id === activeLeftNavItemId);
    }
);

export const getActiveLeftNavItemEffectiveRights = createSelector<
    AppState,
    string,
    ContactFolder[],
    EffectiveRightsType
>(
    'activeLeftNavItemEffectiveRights',
    [
        getActiveLeftNavItemId,
        (appState: AppState) => appState.data.contactFoldersState.publicFolders,
    ],
    (activeLeftNavItemId, publicFolders) => {
        const publicFolder =
            publicFolders && publicFolders.find(folder => folder.id === activeLeftNavItemId);

        return publicFolder?.publicFolderInfo?.effectiveRights;
    }
);

export const isActiveLeftNavItemPublicFolderCanModify = createSelector<
    AppState,
    EffectiveRightsType,
    boolean
>(
    'isActiveLeftNavItemPublicFolderCanModify',
    [getActiveLeftNavItemEffectiveRights],
    effectiveRights => effectiveRights && effectiveRights.Modify === true
);

export const isActiveLeftNavItemPublicFolderCanCreate = createSelector<
    AppState,
    EffectiveRightsType,
    boolean
>(
    'isActiveLeftNavItemPublicFolderCanCreate',
    [getActiveLeftNavItemEffectiveRights],
    effectiveRights => effectiveRights && effectiveRights.CreateContents === true
);

export const isActiveLeftNavItemPublicFolderCanRead = createSelector<
    AppState,
    EffectiveRightsType,
    boolean
>(
    'isActiveLeftNavItemPublicFolderCanRead',
    [getActiveLeftNavItemEffectiveRights],
    effectiveRights => effectiveRights && effectiveRights.Read === true
);

export const getActiveLeftNavItemErrorResponseCode = createSelector<
    AppState,
    string,
    FolderErrorStateMap,
    string
>(
    'getActiveLeftNavItemErrorResponseCode',
    [
        getActiveLeftNavItemId,
        (appState: AppState) => appState.data.contactFoldersState.folderErrorState,
    ],
    (activeLeftNavItemId, folderErrorState) => {
        const error = folderErrorState[activeLeftNavItemId];

        return error?.responseCode;
    }
);

/**
 * This selector returns the "current" contact folder. User actions such as adding a contact etc should use this
 * selector to determine which contact folder the action applies to.
 * This selector does not dictate which folder should be displayed as the "active" entry in the left nav.
 * The left nav may contain entries such as "Favorites" which does not represent a contact folder.
 * If the "All" Tab is selected, all write operations are performed on the default folder.
 */
export const getCurrentWritableContactFolderId = createSelector<
    AppState,
    ContactFolder,
    ContactFolder[],
    string,
    SearchQuery,
    boolean,
    boolean,
    string
>(
    'getCurrentWritableContactFolderId',
    [
        (appState: AppState) => appState.data.contactFoldersState.defaultContactFolder,
        getDisplayedContactFolders,
        (appState: AppState) => appState.view.navigationState.clickedLeftNavItemId,
        (appState: AppState) => appState.view.navigationState.currentQuery,
        isActiveLeftNavItemPublicFolderCanModify,
        isActiveLeftNavItemPublicFolderCanCreate,
    ],
    (
        defaultContactFolder,
        displayedContactFolders,
        clickedLeftNavItemId,
        searchQuery,
        publicFolderCanModify,
        publicFolderCanCreate
    ) => {
        let maybeDisplayedFolder = undefined;

        if (clickedLeftNavItemId && !searchQuery) {
            if (publicFolderCanModify || publicFolderCanCreate) {
                return clickedLeftNavItemId;
            }

            maybeDisplayedFolder = displayedContactFolders.find(
                folder => folder.id === clickedLeftNavItemId && !folder.isReadOnly
            );
        }

        const contactFolder =
            maybeDisplayedFolder ||
            defaultContactFolder ||
            displayedContactFolders.find(folder => !folder.isReadOnly);

        return contactFolder ? contactFolder.id : undefined;
    }
);

/**
 * This selector returns a folder if the current active
 * left nav item is a sparse folder, undefined if not.
 */
export const getActiveLeftNavItemSparseFolder = createSelector<
    AppState,
    string,
    ContactFolder[],
    ContactFolder
>(
    'getActiveLeftNavItemSparseFolder',
    [
        getActiveLeftNavItemId,
        (appState: AppState) => appState.data.contactFoldersState.sparseFolders,
    ],
    (activeLeftNavItemId, sparseFolders) => {
        return sparseFolders && sparseFolders.find(folder => folder.id === activeLeftNavItemId);
    }
);

/**
 * This selector returns the total count of items in the current active left nav item if
 * it is a sparse folder, undefined if not.
 */
export const sparseFolderTotalCount = createSelector<
    AppState,
    ContactFolder,
    SparseFolderStateMap,
    number
>(
    'sparseFolderTotalCount',
    [
        getActiveLeftNavItemSparseFolder,
        (appState: AppState) => appState.data.contactFoldersState.sparseFoldersState,
    ],
    (sparseFolder, sparseFoldersState) => {
        return (
            sparseFolder &&
            sparseFoldersState[sparseFolder.id] &&
            sparseFoldersState[sparseFolder.id].totalCount
        );
    }
);

export { ContactFoldersState } from '..';
